import { useEffect, useRef, useCallback } from 'react';

/**
 * Generic hook to handle polling of table rows based on dynamic conditions
 *
 * This hook provides a flexible polling mechanism that can be used with any table data
 * and custom polling conditions. It's designed to be reusable across different pages.
 *
 * Features:
 * - Dynamic polling conditions via shouldPoll function
 * - Configurable polling intervals
 * - Automatic polling management based on table data changes
 * - Global cleanup function for page changes
 * - Error handling with configurable retry intervals
 * - Visual indicator support through pollingCount
 *
 * Usage:
 * ```javascript
 * const { pollingCount } = useTableRowPolling({
 *   tableData: sources,
 *   shouldPoll: (item) => item.preflight_state === 'pending' || item.preflight_state === 'running',
 *   pollItem: async (itemId) => {
 *     const result = await triggerSourceFetch(itemId);
 *     return result.data;
 *   },
 *   onItemUpdate: (itemId, updatedData) => handleSourceUpdate(itemId, updatedData),
 *   shouldContinuePolling: (updatedData) =>
 *     updatedData.preflight_state === 'pending' || updatedData.preflight_state === 'running',
 *   pollInterval: 10000,
 *   errorRetryInterval: 15000,
 *   globalCleanupFunctionName: 'clearSourcePolling'
 * });
 * ```
 *
 * @param {Object} config - Configuration object
 * @param {Array} config.tableData - Array of table row objects
 * @param {Function} config.shouldPoll - Function to determine if an item should be polled: (item) => boolean
 * @param {Function} config.pollItem - Async function to poll a single item: (itemId) => Promise<data>
 * @param {Function} config.onItemUpdate - Callback when item is updated: (itemId, updatedData) => void
 * @param {Function} config.shouldContinuePolling - Function to determine if polling should continue: (updatedData) => boolean
 * @param {number} [config.pollInterval=10000] - Polling interval in milliseconds
 * @param {number} [config.errorRetryInterval=15000] - Retry interval on error in milliseconds
 * @param {string} [config.globalCleanupFunctionName='clearTablePolling'] - Name of global cleanup function
 * @param {string} [config.itemIdField='id'] - Field name to use as item ID
 * @param {string} [config.childrenField='children'] - Field name for nested children
 * @param {string} [config.isGroupField='isGroup'] - Field name to identify group rows
 * @returns {Object} - Object with polling control functions
 */
export const useTableRowPolling = ({
    tableData,
    shouldPoll,
    pollItem,
    onItemUpdate,
    shouldContinuePolling,
    pollInterval = 10000,
    errorRetryInterval = 15000,
    globalCleanupFunctionName = 'clearTablePolling',
    itemIdField = 'id',
    childrenField = 'children',
    isGroupField = 'isGroup',
}) => {
    const pollingIntervals = useRef(new Map());

    const startPolling = useCallback(
        (itemId) => {
            // Don't start polling if already polling this item
            if (pollingIntervals.current.has(itemId)) {
                return;
            }

            const pollSingleItem = async () => {
                try {
                    const updatedData = await pollItem(itemId);

                    if (updatedData) {
                        // Update the item in the table data
                        onItemUpdate(itemId, updatedData);

                        // Check if we should continue polling
                        if (shouldContinuePolling(updatedData)) {
                            // Continue polling after specified interval
                            const timeoutId = setTimeout(pollSingleItem, pollInterval);
                            pollingIntervals.current.set(itemId, timeoutId);
                        } else {
                            // Stop polling - condition no longer met
                            clearPolling(itemId);
                        }
                    }
                } catch (error) {
                    console.log(`Error polling item ${itemId}:`);
                    // Continue polling even on error, but with a longer interval
                    const timeoutId = setTimeout(pollSingleItem, errorRetryInterval);
                    pollingIntervals.current.set(itemId, timeoutId);
                }
            };

            // Start the first poll
            pollSingleItem();
        },
        [pollItem, onItemUpdate, shouldContinuePolling, pollInterval, errorRetryInterval],
    );

    const clearPolling = useCallback((itemId) => {
        const timeoutId = pollingIntervals.current.get(itemId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            pollingIntervals.current.delete(itemId);
        }
    }, []);

    const clearAllPolling = useCallback(() => {
        pollingIntervals.current.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        pollingIntervals.current.clear();
    }, []);

    // Effect to manage polling based on table data
    useEffect(() => {
        if (!tableData || !Array.isArray(tableData)) {
            return;
        }

        // Get all items that need polling
        const itemsToPoll = [];

        // Flatten table data (handle both groups and individual items)
        tableData.forEach((item) => {
            if (item[isGroupField] && item[childrenField]) {
                // Process group children
                item[childrenField].forEach((child) => {
                    if (shouldPoll(child) && child[itemIdField]) {
                        itemsToPoll.push(child[itemIdField]);
                    }
                });
            } else if (!item[isGroupField] && shouldPoll(item) && item[itemIdField]) {
                // Process individual items
                itemsToPoll.push(item[itemIdField]);
            }
        });

        // Start polling for items that need it and aren't already being polled
        itemsToPoll.forEach((itemId) => {
            if (!pollingIntervals.current.has(itemId)) {
                startPolling(itemId);
            }
        });

        // Stop polling for items that no longer need it
        const currentlyPolling = Array.from(pollingIntervals.current.keys());
        currentlyPolling.forEach((itemId) => {
            if (!itemsToPoll.includes(itemId)) {
                clearPolling(itemId);
            }
        });
    }, [tableData, shouldPoll, startPolling, clearPolling, itemIdField, childrenField, isGroupField]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearAllPolling();
        };
    }, [clearAllPolling]);

    // Setup global cleanup function for page changes
    useEffect(() => {
        // Add this polling instance to global tracking
        const globalInstancesKey = `${globalCleanupFunctionName}Instances`;

        if (!window[globalInstancesKey]) {
            window[globalInstancesKey] = [];
        }

        const pollingInstance = {
            clearAllPolling,
            pollingIntervals: pollingIntervals.current,
        };

        window[globalInstancesKey].push(pollingInstance);

        // Global cleanup function that clears all Backbone polling instances
        window[globalCleanupFunctionName] = function () {
            console.log(`${globalCleanupFunctionName} called`);
            if (window[globalInstancesKey]) {
                window[globalInstancesKey].forEach((instance) => {
                    instance.clearAllPolling();
                });
                window[globalInstancesKey] = [];
            }
        };

        return () => {
            // Remove this instance from global tracking on cleanup
            if (window[globalInstancesKey]) {
                const index = window[globalInstancesKey].indexOf(pollingInstance);
                if (index > -1) {
                    window[globalInstancesKey].splice(index, 1);
                }
            }

            // If this was the last instance, clean up the global function
            if (window[globalInstancesKey] && window[globalInstancesKey].length === 0) {
                delete window[globalCleanupFunctionName];
                delete window[globalInstancesKey];
            }
        };
    }, [clearAllPolling, globalCleanupFunctionName]);

    return {
        startPolling,
        clearPolling,
        clearAllPolling,
        pollingCount: pollingIntervals.current.size,
    };
};
